import React, { useMemo } from 'react';
import { Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { capitalize } from '../../functions';
import { Typography } from '../../atoms';
import { ETypography, ETypographyColor } from '../../types/theme';
import { Checkbox } from '../Checkbox';
import { BnIcon } from '../icons_v2';
import { AdditionalsConnectorEnum, IOption, AdditionalRow } from '../../types';
import { StyledSelectOption_v2 } from '../Options_v2/style';
import { OptionThumbnail } from '../Options_v2/OptionThumbnail';
import { ISelectOptions } from '../Options_v2/types';

const RightContentDiv = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	margin-left: auto;
`;

interface OptionProps {
	colors: Theme;
	index: number;
	opt: IOption;
	selectedValuesArray: string[] | number[] | Date[];
	value: string | string[] | number[] | Date[];
	optionVal: string;
	size: 'S' | 'M';
	lastOptionElementRef?: (node: Element) => void;
	nativelyDisabled?: boolean;  // determines if the option is disabled regardless of the selected state, useful for the select all option
}

export const SelectOption = ({
	colors,
	index,
	size,
	onSelect,
	opt,
	autoFocus,
	isMultiple,
	selectedValuesArray,
	value,
	optionVal,
	isAllSelected,
	lastOptionElementRef,
	nativelyDisabled,
}: Omit<ISelectOptions, 'size' | 'options'> & OptionProps) => {
	const onClick = () => {
		if (!opt.isDisabled) onSelect(opt.value);
	};

	const labelComponent = useMemo(() => {
		//TODO: add ellipsis to label
		return (
			<div className="first-row">
				{opt.icon && <BnIcon icon={opt.icon} />}
				<Typography
					color={opt.isDisabled ? ETypographyColor.disabled : ETypographyColor.primary}
					type={ETypography.body2}
				>
					{capitalize(opt.label)}
				</Typography>
				{opt.additionalInfo && (
					<Typography
						color={opt.isDisabled ? ETypographyColor.disabled : ETypographyColor.secondary}
						type={opt.defaultRow?.additionalTypographyType || ETypography.body2}
						className="additional-info"
					>
						{opt.additionalInfo}
					</Typography>
				)}
			</div>
		);
	}, [opt]);

	const additionalRowsComponent = useMemo(() => {
		if (!opt.additionalRows?.length) return null;

		return opt.additionalRows.map((row: AdditionalRow, rowIndex: number) => {
			const { additionals, additionalsConnector, color, type } = row;
			if (!additionals?.length) return null;

			const displayColor: ETypographyColor =
				color ?? (opt.isDisabled ? ETypographyColor.disabled : ETypographyColor.secondary);
			const displayType: ETypography = type ?? ETypography.overline;

			return (
				<div
					className="additional-row"
					key={`${String(opt.value)}-additional-row-${rowIndex}`}
					data-testid={row.dataTestId}
				>
					<Typography color={displayColor} type={displayType}>
						{additionals[0]}
					</Typography>
					{additionals[1] && (
						<>
							<Typography color={displayColor} type={displayType} className="connector">
								{additionalsConnector || AdditionalsConnectorEnum.DASH}
							</Typography>
							<Typography color={displayColor} type={displayType}>
								{additionals[1]}
							</Typography>
						</>
					)}
				</div>
			);
		});
	}, [opt]);

	const optionValString = String(optionVal);
	const selectedSet = useMemo(() => new Set(selectedValuesArray.map(String)), [selectedValuesArray]);

	const isChecked = useMemo(() => {
		if (nativelyDisabled && isAllSelected) return false;
		const isOptionSelected = selectedSet.has(optionValString);
		return isAllSelected ? !isOptionSelected : isOptionSelected;
	}, [selectedSet, optionValString, isAllSelected, value, nativelyDisabled]);

	return (
		<StyledSelectOption_v2
			theme={{ ...colors }}
			key={index}
			onClick={onClick}
			tabIndex={0}
			size={size}
			autoFocus={autoFocus ? index === 0 : false}
			isDisabled={opt.isDisabled}
			ref={lastOptionElementRef}
			isMultiple={isMultiple}
			hasThumbnail={Boolean(opt.image)}
			data-testid={opt.dataTestId || 'select-option'}
			data-value={String(opt.value)}
			data-addon-id={opt.dataAddonId}
		>
			<div className="option-container">
				{isMultiple && (
					<Checkbox
						checkboxPositionTop={8} // Looks good in bo but not in storybook
						data-aid="Select_v2-checkbox"
						width={16}
						functionDisable
						setChecked={() => { }}
						checked={isChecked}
						isDisabled={opt.isDisabled}
					/>
				)}
				{opt.image && <OptionThumbnail image={opt.image} alt={opt.label} />}
				<div className="wrapper" data-aid="Select_v2-span">
					{labelComponent}
					{additionalRowsComponent}
				</div>
				{!!opt.rightContent && (
					<RightContentDiv data-testId="right-content-container">{opt.rightContent}</RightContentDiv>
				)}
			</div>
		</StyledSelectOption_v2>
	);
};
